import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import { TextInput } from '@jotta/ui/TextInput'
import { useForm } from '@jotta/hooks/useForm'
import { CTAButton } from './CTAButton'
import { TermsAndConditionsCheckbox } from './TermsAndConditionsCheckbox'
import {
  customerGroupCodeField,
  newsletterField,
  useAcceptedTermsField,
  useActivationCodeTerminology,
  useEmailField,
  useNameField,
  usePasswordField,
  useVoucherCodeField,
} from './signupForm'
import { Form } from '@jotta/ui/Form'
import { FormField } from '@jotta/ui/FormField'
import type { SignupRequestCallback } from './CreateUser'
import { PasswordField } from './PasswordField'
import { cn } from '@jotta/utils/css'

export function CreatePrivateUser({
  className,
  ctaClassName,
  isPending,
  signup,
  onRequestLogin,
  supportsVoucher,
  prefill,
  ctaLabelText,
  referralCode,
}: {
  className: string
  ctaClassName?: string
  isPending: boolean
  signup: SignupRequestCallback
  supportsVoucher: boolean
  onRequestLogin?: () => void
  prefill: { customerGroupCode: string; name?: string; email?: string }
  ctaLabelText?: string
  referralCode?: string
}) {
  const activationCodeTerminology = useActivationCodeTerminology()
  const { customerGroupCode } = prefill
  const form = useForm({
    fields: {
      name: useNameField(),
      email: useEmailField(customerGroupCode, onRequestLogin),
      password: usePasswordField(),
      customerGroupCode: customerGroupCodeField,
      voucherCode: useVoucherCodeField(
        customerGroupCode,
        voucherCustomerGroupCode =>
          form.set('customerGroupCode', voucherCustomerGroupCode),
      ),
      acceptedTerms: useAcceptedTermsField(),
      newsletter: newsletterField,
    },
    suppressIndicatorsForRequiredFields: true,
    defaultValues: () => prefill,
    submit: ({
      name,
      email,
      password,
      voucherCode,
      newsletter,
      customerGroupCode,
    }) =>
      signup({
        request: {
          case: 'formSignup',
          value: {
            fullname: name,
            email: email,
            password: password,
          },
        },
        customerGroupCode,
        voucherCode,
        wantsNewsletter: Boolean(newsletter),
        referralCode: referralCode,
      }),
  })

  const {
    name,
    password,
    email,
    voucherCode,
    acceptedTerms,
    newsletter,
    onSubmit,
    isSubmitting,
  } = form

  return (
    <Form form={form} className={className} onSubmit={onSubmit}>
      <FormField label={t`Name`} field={name}>
        <TextInput {...name.inputProps} />
      </FormField>

      <FormField label={t`Email`} field={email}>
        <TextInput {...email.inputProps} />
      </FormField>

      <PasswordField field={password} />

      {supportsVoucher && (
        <FormField
          label={
            activationCodeTerminology ? t`Activation code` : t`Voucher code`
          }
          field={voucherCode}
        >
          <TextInput {...voucherCode.inputProps} />
        </FormField>
      )}

      <TermsAndConditionsCheckbox
        acceptedTerms={acceptedTerms}
        newsletter={newsletter}
      />

      <CTAButton
        className={cn('w-full max-w-[309px] self-center', ctaClassName)}
        type="submit"
        loading={isPending || isSubmitting}
      >
        {ctaLabelText || <Trans>Create account</Trans>}
      </CTAButton>
    </Form>
  )
}
